import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { scoreLabel, USE_CASES } from "@/lib/constants";
import { useSeo } from "@/hooks/useSeo";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1569258592171-357ea26da4df?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_STORAGE =
  "https://images.unsplash.com/photo-1470880587080-599f3e4f0913?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_WINE =
  "https://images.unsplash.com/photo-1554598286-ed7bfd1dedca?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_ESCAPE =
  "https://images.unsplash.com/photo-1565626424178-c699f6601afd?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_PODCAST =
  "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_EVENT =
  "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_KITCHEN =
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_SPORT =
  "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAT_SHOWROOM =
  "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const [listings, setListings] = useState([]);
  useSeo({
    title: "Louez vos m² inutilisés en sous-sol",
    description: "R-1.immo — La marketplace des sous-sols, caves, parkings et locaux atypiques. Valorisez vos m² invisibles ou trouvez un lieu compatible avec votre activité : escape game, dark kitchen, studio, événementiel, stockage.",
    path: "/",
  });

  useEffect(() => {
    api.get("/listings?limit=4").then((r) => setListings(r.data)).catch(() => setListings([]));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden border-b border-zinc-900">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/20" />
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-zinc-950 to-transparent" />

        <div className="relative w-full px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
          <div className="flex items-center gap-3 mb-8 animate-fadeUp">
            <div className="w-8 h-px bg-amber-500" />
            <span className="label-mono text-amber-500">Marketplace · sous-sols atypiques</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-7xl lg:text-8xl font-medium tracking-tighter max-w-5xl leading-[0.9] mb-8 animate-fadeUp"
            style={{ animationDelay: "0.1s" }}
          >
            Louez vos m²
            <br />
            inutilisés
            <br />
            <span className="text-amber-500">en sous-sol.</span>
          </h1>

          <p className="text-lg text-zinc-300 max-w-xl leading-relaxed mb-12 animate-fadeUp" style={{ animationDelay: "0.2s" }}>
            Valorisez sous-sols, caves, parkings, réserves, locaux techniques et espaces incroyables inexploités. Des espaces oubliés qui deviennent studios, dark kitchens, escape games, showrooms.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 animate-fadeUp" style={{ animationDelay: "0.3s" }}>
            <Link
              to="/deposer"
              data-testid="cta-deposer-hero"
              className="group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-6 py-4 text-sm uppercase tracking-widest font-medium transition-colors"
            >
              Proposer un R-1
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/annonces"
              data-testid="cta-trouver-hero"
              className="group inline-flex items-center gap-3 border border-zinc-700 hover:border-amber-500 text-zinc-100 px-6 py-4 text-sm uppercase tracking-widest transition-colors"
            >
              Trouver un lieu
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl animate-fadeUp" style={{ animationDelay: "0.4s" }}>
            {[
              { k: "1 200+", v: "m² référencés" },
              { k: "16", v: "usages compatibles" },
              { k: "-40%", v: "vs. surface équivalente" },
            ].map((s) => (
              <div key={s.v} className="border-l border-zinc-800 pl-4">
                <div className="font-display text-3xl">{s.k}</div>
                <div className="label-mono mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH BY USE — BENTO */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-b border-zinc-900">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="label-mono mb-4 text-amber-500">§ 01 · usages</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-2xl">
              Je cherche un lieu pour…
            </h2>
          </div>
          <Link to="/recherche-par-usage" className="text-sm text-zinc-400 hover:text-amber-500 label-mono">
            Tous les usages →
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
          {[
            { key: "escape_game", label: "Escape game", img: CAT_ESCAPE },
            { key: "stockage", label: "Stockage", img: CAT_STORAGE },
            { key: "cave_vin", label: "Cave à vin", img: CAT_WINE },
            { key: "dark_kitchen", label: "Dark kitchen", img: CAT_KITCHEN },
            { key: "studio_podcast", label: "Studio podcast", img: CAT_PODCAST },
            { key: "evenementiel", label: "Événementiel", img: CAT_EVENT },
            { key: "salle_sport", label: "Salle de sport", img: CAT_SPORT },
            { key: "showroom", label: "Showroom", img: CAT_SHOWROOM },
          ].map((u) => (
            <Link
              key={u.key}
              to={`/annonces?use_case=${u.key}`}
              data-testid={`use-${u.key}`}
              className="group relative aspect-[4/3] overflow-hidden bg-zinc-950"
            >
              <div
                className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-all duration-700"
                style={{ backgroundImage: `url(${u.img})` }}
              />
              <div className="absolute inset-0 bg-zinc-950/60 group-hover:bg-zinc-950/30 transition-colors" />
              <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end">
                <div className="label-mono mb-1 text-zinc-400 group-hover:text-amber-500 transition-colors">
                  → {u.key.replace("_", " ")}
                </div>
                <div className="font-display text-xl md:text-2xl">{u.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LATEST LISTINGS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-b border-zinc-900">
        <div className="flex justify-between items-end mb-16">
          <div>
            <div className="label-mono mb-4 text-amber-500">§ 02 · derniers arrivages</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Sélection récente</h2>
          </div>
          <Link to="/annonces" className="text-sm label-mono hover:text-amber-500">Toutes les annonces →</Link>
        </div>
        {listings.length === 0 ? (
          <div className="border border-zinc-800 p-16 text-center">
            <div className="label-mono mb-4">Aucune annonce publiée pour l'instant</div>
            <Link to="/deposer" className="inline-block bg-amber-500 text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-amber-400">
              Soyez le premier à déposer
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
            {listings.map((l) => (
              <ListingCardMini key={l.id} listing={l} />
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-b border-zinc-900">
        <div className="label-mono mb-4 text-amber-500">§ 03 · méthode</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-16">
          1 lieu : 2 intérêts.
        </h2>

        <div className="grid md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
          <div className="bg-zinc-950 p-8 md:p-12">
            <div className="label-mono mb-6 text-zinc-500">POUR LES PROPRIÉTAIRES</div>
            <h3 className="font-display text-2xl mb-4">Transformez du m² dormant en revenu récurrent</h3>
            <ul className="space-y-3 text-zinc-300 text-sm mt-8">
              <li>· Dépôt guidé en 9 étapes techniques</li>
              <li>· Score d'exploitabilité auto-calculé</li>
              <li>· Validation qualité avant publication</li>
              <li>· Leads qualifiés uniquement</li>
            </ul>
            <Link to="/proprietaires" className="inline-flex items-center gap-2 mt-8 text-amber-500 text-sm label-mono hover:gap-3 transition-all">
              En savoir plus <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-zinc-950 p-8 md:p-12">
            <div className="label-mono mb-6 text-zinc-500">POUR LES EXPLOITANTS</div>
            <h3 className="font-display text-2xl mb-4">Trouvez le lieu que personne ne cherche</h3>
            <ul className="space-y-3 text-zinc-300 text-sm mt-8">
              <li>· Filtres par usage compatible</li>
              <li>· Fiches techniques exhaustives</li>
              <li>· Statut ERP et contraintes visibles</li>
              <li>· Contact direct avec le propriétaire</li>
            </ul>
            <Link to="/exploitants" className="inline-flex items-center gap-2 mt-8 text-amber-500 text-sm label-mono hover:gap-3 transition-all">
              En savoir plus <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-32 text-center">
        <div className="label-mono mb-4 text-amber-500">§ 04</div>
        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter max-w-4xl mx-auto leading-[0.95]">
          Des espaces oubliés. Des opportunités pour des projets de proximité.
        </h2>
        <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/deposer" className="bg-amber-500 text-black px-8 py-4 text-xs uppercase tracking-widest hover:bg-amber-400 transition-colors">
            Proposer un R-1
          </Link>
          <Link to="/annonces" className="border border-zinc-700 hover:border-amber-500 px-8 py-4 text-xs uppercase tracking-widest">
            Explorer les annonces
          </Link>
        </div>
      </section>
    </div>
  );
}

function ListingCardMini({ listing }) {
  const s = scoreLabel(listing.score || 0);
  return (
    <Link
      to={`/annonces/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group bg-zinc-950 hover:bg-zinc-900 transition-colors block"
    >
      <div className="aspect-[4/3] bg-zinc-900 overflow-hidden relative">
        {listing.photos?.[0] ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${listing.photos[0]}`}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center label-mono text-zinc-700">
            [ SANS · IMAGE ]
          </div>
        )}
        <div className="absolute top-3 left-3 label-mono bg-zinc-950/80 backdrop-blur-sm px-2 py-1">
          {listing.city}
        </div>
        <div className={`absolute top-3 right-3 font-mono-tech text-xs px-2 py-1 bg-zinc-950/80 ${s.color}`}>
          [ {listing.score || 0} / 100 ]
        </div>
      </div>
      <div className="p-4">
        <div className="font-display text-lg leading-tight mb-2 group-hover:text-amber-500 transition-colors">{listing.title}</div>
        <div className="flex items-center gap-3 label-mono text-zinc-500">
          <span>{listing.surface_exploitable || listing.surface_total}m²</span>
          <span>·</span>
          <span>H{listing.ceiling_height_min}m</span>
          {listing.monthly_rent > 0 && <><span>·</span><span>{listing.monthly_rent}€/mo</span></>}
        </div>
      </div>
    </Link>
  );
}
